import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, ContentTemplate, Divider, Typography } from 'web-library';
import { useHistory, useLocation } from 'react-router-dom';



import { makeStyles } from 'tss-react/mui';
import Link from "@mui/material/Link";

import DocumentBadge from '../../../../components/DocumentBadge';
import ContactDetails from '../particulars/ContactDetails';
import NextOfKinContactDetails from '../particulars/NextOfKinContactDetails';
import ParentParticulars from '../particulars/ParentParticulars';
import PersonalDetails from '../particulars/PersonalDetails';
import CAECertificateDetails from './CAECertificateDetails';
import CEQScore from './CEQScore';
import ExamDetails from './ExamDetails';
import ExamFee from './ExamFee';
import PaymentDetails from './PaymentDetails';
import PersonDeclaring from './PersonDeclaring';
import RegistrationHeaderDetails from './RegistrationHeaderDetails';
import SchoolDetails from './SchoolDetails';
import SiblingDetails from './SiblingDetails';
import CandidateDetails from './spers/CandidateDetails';
import RouteKey from '../../../../routes/key';
import { ParentParticularsLocation } from '../../../../pages/examRegistration/NonNationalExam/AEIS/ParentParticulars';

const { exam, withdrawal } = RouteKey.child;

const { examRegistrations, uploadAdditionalResources } = RouteKey.exam.child;

const { nonNationalInstruction } = RouteKey.withdrawal.child;

interface IConfirmAndPayList {
  data: any;
  isAEIS: boolean;
  refetch?: () => void;
  isFetching?: boolean;
  isTransactionHistory?: boolean;
}

const useStyles = makeStyles()((theme) =>
({
  container: {
    alignItems: 'center',
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  dialogBold: {
    fontWeight: 'bold',
  },
  buttonContainer: {
    marginBottom: theme.spacing(7),
  },
  paymentButton: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  button: {
    height: 'fit-content',
    marginRight: theme.spacing(2),
  },
  tableCaption: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(0),
    display: 'flex',
  },
  examFeeDetails: {
    paddingBottom: theme.spacing(6),
  },
  selectedSubjects: {
    paddingTop: theme.spacing(6),
  },
  amendmentError: {
    marginBottom: theme.spacing(3),
  },
  subheader: {
    marginBottom: theme.spacing(6),
  },
  documentBadgeContainer: {
    marginTop: theme.spacing(4),
  },
  inputSection: {
    marginBottom: theme.spacing(4),
  },
  viewLog: {
    marginTop: theme.spacing(6),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      paddingLeft: 0,
    },
  },
  bottomButtonGroup: {
    marginBottom: theme.spacing(7),
  },
}));

const RegistrationSummaryList = ({
  data,
  isAEIS,
  refetch,
  isFetching,
  isTransactionHistory,
}: IConfirmAndPayList) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const { state: stateLocation } = useLocation<ParentParticularsLocation>();

  return (
    <>
      <ContentTemplate isContinuable type="full">
        <RegistrationHeaderDetails
          onDownloadAction={() => refetch?.()}
          isDownloading={isFetching}
          applicationStatus={data.applicationStatus}
          entryProofDownloadModels={data.entryProofDownloadModels}
        />

        <PersonalDetails
          examCandidateDetail={data.candidateInfo}
          sameRowName
          isAEIS={isAEIS}
          isSPERS={!isAEIS}
        />
        <ContactDetails
          contactDetails={data.candidateInfo}
          sameRowPhone
          sameRowAddress
          isAEIS={isAEIS}
          isSPERS={!isAEIS}
        />
        <NextOfKinContactDetails {...data.candidateInfo} />
        {isAEIS && (
          <ParentParticulars
            parentDetail={{
              fatherDetail: data.fatherDetail,
              motherDetail: data.motherDetail,
            }}
          />
        )}
        <PersonDeclaring
          personDetails={{
            personDeclaring: data.personDeclaring,
            personDeclaringId: data.personDeclaringId,
          }}
        />
        {!isAEIS && (
          <>
            <Divider paddingTop />
            <CandidateDetails
              candidateDetails={{
                certificateNo: data.candidateInfo.birthCertNo,
                dateAttained: data.candidateInfo.sprAttainedDate,
                motherTongueLanguage: data.mtLanguage,
              }}
            />
          </>
        )}
        <Divider paddingTop />
        <ExamDetails
          examDetails={{
            examYear: data.examYear,
            admissionLevel: data.admissionLevel,
            examLevel: data.examLevel,
            mtLanguage: data.mtLanguage,
            isAEIS,
          }}
        />
        <Divider paddingTop />
        {isAEIS && (
          <>
            {data.caeCertificationDetail && (
              <CAECertificateDetails certificateDetails={data.caeCertificationDetail} />
            )}
            {data.ceqScoreDetail && <CEQScore score={data.ceqScoreDetail} />}
          </>
        )}
        <SchoolDetails schoolDetails={data.schoolDetails} isAEIS={isAEIS} />
        {isAEIS && (
          <SiblingDetails siblingDetails={data.siblingDetails} examType={data.examLevel} />
        )}
        {data.supportingDocument.length ? (
          <>
            <Divider paddingTop />
            <Typography variant="h3" className={classes.subheader}>
              {t('exam->>NonNationalExam.SupportingDocuments')}
            </Typography>
            {data.supportingDocument.map((item: string) => (
              <DocumentBadge
                key={item}
                className={classes.documentBadgeContainer}
                documentName={item}
              />
            ))}
          </>
        ) : null}
        <Divider paddingTop />
        <ExamFee
          feeDetails={{
            regNumber: data.gstRegistrationNumber,
            totalExamFee: data.payment.totalExamFee,
            totalGst: data.payment.totalGst,
            totalFee: data.payment.totalFee,
          }}
        />
        <Divider paddingTop />
        <PaymentDetails paymentDetails={data.payment} />
      </ContentTemplate>

      <ContentTemplate continuous>
        <Box className={classes.buttonContainer}>
          <Button
            className={classes.button}
            variant="outlined"
            onClick={() =>
              history.push(`/${exam}/${examRegistrations.route}`, {
                from: isTransactionHistory ? 'TransactionHistoryPages' : null,
              })
            }
          >
            {isTransactionHistory
              ? t('exam->>RegistrationSummary.BackToTransactionHistory')
              : t('exam->>RegistrationSummary.BackToRegisteredExams')}
          </Button>
          {data.applicationStatus?.applicationStatus === 'INCOMPLETE' && (
            <Button
              className={classes.button}
              variant="contained"
              onClick={() => history.push(`${uploadAdditionalResources.route}`, stateLocation)}
            >
              {t('exam->>RegistrationSummary.UploadAdditionalDocuments')}
            </Button>
          )}
          {data.withdrawalWindowOpened && !isTransactionHistory && (
            <Box className={classes.tableCaption}>
              <Typography variant="caption" className={classes.dialogBold}>
                {t('exam->>RegistrationSummary.WithdrawInstruction')}&nbsp;
              </Typography>
              <Link
                id="withdrawal-link"
                onClick={() => {
                  history.push(
                    `/${withdrawal}/${examRegistrations.route}/${nonNationalInstruction.route}`
                  );
                }}
              >
                <Typography variant="caption" className={classes.dialogBold}>
                  {t('exam->>ResultDetail.click here.')}</Typography>
              </Link>
            </Box>
          )}
        </Box>
      </ContentTemplate>
    </>
  );
};

export default RegistrationSummaryList;
